import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import HeadingInspector from '@/components/HeadingInspector'
import { lightTheme } from '@/customTheme'
import { selectUser } from '@/store/slices/user/selectors'
import { HorizontalSeparator } from '../../../FloatingInspectorButton/SharedComponents/Popover/HorizontalSeparator'
import { setOpenModalTryPremium } from '@/store/slices/user/actions'
import { useStyletron } from 'styletron-react'
import { useAppDispatch } from '@/store/store'
import { SignInManager } from '../SignInManager'
import { formatAICreditsResetDate } from '@/utils/formatAICreditsResetDate'

function getSliderColor(pct: number): string {
  if (pct > 0.2) return '#0db08c'
  if (pct > 0.05) return '#FCD34D'
  return '#FF0505'
}

interface AIUsageMenuProps {
  handleNavigation: () => void
  closeMenu: () => void
}

export default function AIUsageMenu({ handleNavigation, closeMenu }: AIUsageMenuProps) {
  const { t, i18n } = useTranslation()
  const user = useSelector(selectUser)
  const dispatch = useAppDispatch()

  useEffect(() => {
    SignInManager.getInstance().refreshCurrentUserProfileFromServer()
  }, [])

  const remaining = Number(user?.remaining_credits ?? 0)
  const total = Number(user?.total_monthly_credits ?? 0)
  const pct = total > 0 ? Math.max(0, Math.min(1, remaining / total)) : 1
  const sliderColor = getSliderColor(pct)
  const resetDate = formatAICreditsResetDate(user?.next_credits_reset, i18n.language)
  const isSuper = Boolean(user?.userIsSuper)

  return (
    <>
      <HeadingInspector
        hasNavigation={true}
        handleNavigation={handleNavigation}
        title={t('AI credits')}
        style={{ padding: '0 0 16px 0' }}
      />
      <div
        style={{
          backgroundColor: lightTheme.colors.grayScale50,
          borderRadius: '12px',
          padding: '20px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          width: '100%',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ ...lightTheme.typography.Small14regular, color: lightTheme.colors.blackGray }}>
            {total > 0 ? `${Math.round(pct * 100)}%` : '–'}
          </span>
          <span style={{ ...lightTheme.typography.Small14regular, color: lightTheme.colors.blackGray }}>
            {total > 0 ? t('{{used}}/{{total}} used', { used: total - remaining, total }) : '–'}
          </span>
        </div>
        <div
          style={{
            height: '8px',
            borderRadius: '100px',
            backgroundColor: lightTheme.colors.grayScale100,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              bottom: 0,
              borderRadius: '100px',
              backgroundColor: sliderColor,
              width: `${pct * 100}%`,
              transition: 'width 0.3s ease',
            }}
          />
        </div>
      </div>
      {resetDate && (
        <p
          style={{
            ...lightTheme.typography.Small12regular,
            color: lightTheme.colors.grayScale600,
            margin: '0',
            marginTop: '12px',
          }}
        >
          {t('Resets on {{date}}', { date: resetDate })}
        </p>
      )}
      <p
        style={{
          ...lightTheme.typography.Small14regular,
          color: lightTheme.colors.grayScale600,
          textDecoration: 'underline',
          cursor: 'pointer',
          margin: '0',
          marginTop: '12px',
        }}
        onClick={() =>
          window.open(
            'https://support.bazaart.com/en/content/why-is-there-a-usage-limit-on-advanced-ai-tools-in-bazaart',
            '_blank'
          )
        }
      >
        {t('Learn more')}
      </p>
      {!isSuper && (
        <>
          <HorizontalSeparator style={{ margin: '20px 0' }} />
          <UpgradeToSuperButton
            label={t('Upgrade to Super')}
            onClick={() => {
              dispatch(setOpenModalTryPremium({ isOpen: true, source: 'Header_CTA_Super' }))
              closeMenu()
            }}
          />
        </>
      )}
    </>
  )
}

function UpgradeToSuperButton({ label, onClick }: { label: string; onClick: () => void }) {
  const [css] = useStyletron()

  return (
    <button
      onClick={onClick}
      className={css({
        background: 'linear-gradient(91deg, #7508FF 1.26%, rgba(198, 4, 201, 0.80) 99.03%)',
        borderRadius: '100px',
        height: '40px',
        width: '100%',
        color: '#fff',
        fontWeight: 600,
        fontSize: '14px',
        border: 'none',
        cursor: 'pointer',
        letterSpacing: '-0.084px',
        transition: 'box-shadow 0.2s ease',
        ':hover': {
          boxShadow: '0px 0px 20px 0px rgba(157,1,244,0.5)',
        },
      })}
    >
      {label}
    </button>
  )
}